import {
    AdditionalInfo,
    addPrefixToAddInfoSchemaKeys,
    ProfilePicSizes,
    ProfileShareSection,
    transformSchemaKeys,
} from '@eventr/common'
import {
    CheckboxField,
    ColoursObj,
    FormInputCountrySelectorV2,
    FormInputText,
    FormMobileDateOfBirthPicker,
    Icon,
    IIpInfo,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
    useIpInfo,
} from '@eventr/common-storybook'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Stack } from '@mui/material'
import { useEffect } from 'react'
import {
    Controller,
    FormProvider,
    SubmitHandler,
    useForm,
    useWatch,
} from 'react-hook-form'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { NavigationView } from '../../layout/NavigationView'
import { getMediaSizes } from '../../media/media.slice'
import { FormAccountAvatarStack } from '../accountPage/accountAvatarStack'
import ProfileShareSectionFinder from './profileShareSectionFinder'
import {
    updateUserData,
    UserDetailsSchema,
    UserValidationSchema,
} from './userProfileUtils'

export const EditDetailsPage = () => {
    const { profile, selectedOrg } = useBoundStore(
        useShallow((s) => {
            return {
                profile: s.profile,
                selectedOrg: s.selectedOrg,
            }
        })
    )

    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            replaceDialog: s.replaceDialog,
            clearDialog: s.clearDialog,
        }))
    )

    const shapedUserAddInfo = profile?.additionalInfo
        ? addPrefixToAddInfoSchemaKeys(
            profile?.additionalInfo as AdditionalInfo
        )
        : {}

    const enabledSections: ProfileShareSection[] = (
        bs()?.getProfileShareSections() ?? []
    ).filter((s) => s.enabled)

    const unenabledSections: ProfileShareSection[] = (
        bs()?.getProfileShareSections() ?? []
    ).filter((s) => s.enabled === false)

    const profileKeys = Object.keys(profile?.additionalInfo ?? {})

    const hiddenSections = unenabledSections.filter((section) => {
        return profileKeys.includes(section.id)
    })

    const totalSections = enabledSections.concat(hiddenSections ?? [])

    const currentOrg = selectedOrg

    const methods = useForm<UserValidationSchema>({
        resolver: zodResolver(UserDetailsSchema(totalSections ?? [])),
    })

    const { handleSubmit, control, setValue } = methods

    const ipInfo: IIpInfo = useIpInfo((s: { ipInfo: any }) => s.ipInfo)

    const orgName = selectedOrg?.friendlyName ?? i18n.t('account.profile.team')

    useEffect(() => {
        if (profile?.title) setValue('title', profile?.title)
        if (profile?.firstName) setValue('firstName', profile?.firstName)
        if (profile?.lastName) setValue('lastName', profile?.lastName)
        if (profile?.phoneNumber) setValue('phoneNumber', profile?.phoneNumber)
        if (profile?.dateOfBirth) setValue('dateOfBirth', profile?.dateOfBirth)
        if (profile?.email) setValue('email', profile?.email)
        if (profile?.countryCode) {
            setValue('countryCode', profile?.countryCode)
        } else setValue('countryCode', ipInfo?.countryCode)
        if (profile?.profilePicUrl)
            setValue('profilePic', profile?.profilePicUrl)
        if (profile?.marketingAcceptedUnix)
            setValue('marketingOptIn', profile?.marketingAcceptedUnix > 0)
        if (currentOrg) setValue('targetOrg', currentOrg.name)
        if (currentOrg)
            setValue('sharedProperties', currentOrg.sharedProperties)
        if (profile?.additionalInfo)
            setValue('additionalInfo', shapedUserAddInfo)
    }, [profile, setValue, currentOrg])

    const formValues = useWatch({ control })
    const valuesExist = formValues ? Object.keys(formValues).length > 0 : false

    const hasFormChanged =
        valuesExist &&
        JSON.stringify(formValues) !==
        JSON.stringify(profile?.additionalInfo ?? [])

    const onSubmit: SubmitHandler<UserValidationSchema> = async (data) => {
        const formattedData = {
            ...data,
            additionalInfo: transformSchemaKeys(data?.additionalInfo ?? {}),
        }
        const { profilePic, ...rest } = formattedData
        let profilePicUrl: string | null = null
        let profilePicSizes: ProfilePicSizes = {}

        if (profilePic instanceof File) {
            loadingSignal.value = true
            const resp = await useBoundStore
                .getState()
                .uploadMedia(profilePic as File, 'ProfilePic')
            if (resp.status === 'error') {
                loadingSignal.value = false
                defaultAddSnack(
                    resp.message ?? i18n.t('account.errorProfilePic'),
                    'error'
                )
                return false
            } else {
                profilePicUrl = resp.data?.source_url ?? null
                profilePicSizes = resp.data ? getMediaSizes(resp.data) : {}
            }
        }
        const updatedData = {
            ...rest,
            profilePicUrl: profilePicUrl ?? undefined,
            profilePicSizes,
            marketingOptIn: !!formattedData.marketingOptIn,
        }
        if (hasFormChanged) {
            if (await updateUserData(updatedData)) {
                window.goBack()
            }
        } else {
            window.goBack()
        }
    }

    return (
        <FormProvider {...methods}>
            <NavigationView
                profilePage
                navigationBarTitle={i18n.t('account.profile.title')}
                backButtonAction={() => {
                    window.goBack()
                }}
                navigationBarTrailing={[
                    {
                        id: 'saveButton',
                        buttonText: i18n.t('general.done'),
                        colour: ColoursObj.core.primary,
                        useButton: true,
                        action: async () => handleSubmit(onSubmit)(),
                    },
                ]}
                usePageTransition
            >
                <Stack
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Stack
                        style={{
                            height: '180px',
                        }}
                    >
                        <Stack
                            style={{
                                height: '90px',
                            }}
                        >
                            <FormAccountAvatarStack
                                edit={true}
                                profilePage={true}
                                control={control as any}
                                name="profilePic"
                            />
                        </Stack>
                    </Stack>
                    <Stack
                        gap="8px"
                        style={{
                            padding: '0px 24px 0',
                        }}
                    >
                        <FormInputText
                            fieldLabel={i18n.t('admin.title')}
                            control={control as any}
                            name="title"
                        />
                        <FormInputText
                            fieldLabel={i18n.t('admin.first-name')}
                            helperText={profile?.firstName}
                            control={control as any}
                            name="firstName"
                            required
                        />
                        <FormInputText
                            fieldLabel={i18n.t('admin.last-name')}
                            helperText={profile?.lastName}
                            control={control as any}
                            name="lastName"
                            required
                        />
                        <FormInputText
                            fieldLabel={i18n.t('account.profile.phoneNumber')}
                            helperText={profile?.phoneNumber}
                            control={control as any}
                            name="phoneNumber"
                            type="tel"
                        />
                        <FormInputText
                            fieldLabel={i18n.t('admin.email')}
                            control={control as any}
                            name="email"
                            type="email"
                        />
                        <FormMobileDateOfBirthPicker
                            fieldLabel={i18n.t(
                                'admin.additionalInformation.dateOfBirth'
                            )}
                            control={control as any}
                            name="dateOfBirth"
                        />
                        <Stack style={{ paddingBottom: '16px' }}>
                            <FormInputCountrySelectorV2
                                name="countryCode"
                                fieldLabel={i18n.t(
                                    'edit-itinerary.fields.country.field-label'
                                )}
                                placeholder={i18n.t(
                                    'edit-itinerary.fields.country.placeholder'
                                )}
                                control={control as any}
                            />
                        </Stack>
                        {/*will be condiationally added if there are sections*/}
                        {totalSections.length !== 0 && (
                            <Stack gap="16px">
                                {enabledSections &&
                                    enabledSections.length > 0 && (
                                        <Stack>
                                            <Box
                                                borderRadius="8px"
                                                height="56px"
                                                marginBottom="8px"
                                                bgcolor={
                                                    ColoursObj.backgrounds
                                                        .secondary
                                                }
                                            >
                                                <Stack
                                                    padding="16px"
                                                    direction="row"
                                                    spacing="8px"
                                                >
                                                    <Icon
                                                        name="Seat"
                                                        height="24px"
                                                        width="24px"
                                                    />
                                                    <Typography
                                                        variant="bodyBold"
                                                        id="BookingDetails"
                                                        color={
                                                            ColoursObj.font
                                                                .primary
                                                        }
                                                    >
                                                        {i18n.t(
                                                            'account.profile.share-with',
                                                            { orgName }
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                            <ProfileShareSectionFinder
                                                sections={enabledSections}
                                                control={control}
                                                orgName-={
                                                    selectedOrg?.friendlyName
                                                }
                                            />
                                        </Stack>
                                    )}
                                {hiddenSections &&
                                    hiddenSections.length > 0 && (
                                        <Stack>
                                            <Box
                                                borderRadius="8px"
                                                height="56px"
                                                marginBottom="8px"
                                                bgcolor={
                                                    ColoursObj.backgrounds
                                                        .secondary
                                                }
                                            >
                                                <Stack
                                                    padding="16px 16px 0 16px"
                                                    direction="row"
                                                    spacing="8px"
                                                    justifyContent="space-between"
                                                >
                                                    <Stack
                                                        direction="row"
                                                        gap="5px"
                                                    >
                                                        <Icon
                                                            name="Seat"
                                                            height="24px"
                                                            width="24px"
                                                        />
                                                        <Typography
                                                            variant="bodyBold"
                                                            id="BookingDetails"
                                                            color={
                                                                ColoursObj.font
                                                                    .primary
                                                            }
                                                        >
                                                            {i18n.t(
                                                                'account.profile.not-shared'
                                                            )}
                                                        </Typography>
                                                    </Stack>
                                                    <Icon
                                                        name="Info"
                                                        iconColour={
                                                            ColoursObj.core
                                                                .secondary
                                                        }
                                                        onClick={() => {
                                                            replaceDialog({
                                                                node: (
                                                                    <MobileConfirmDialog
                                                                        text={i18n.t(
                                                                            'account.profile.not-shared-info'
                                                                        )}
                                                                        onCancel={() => {
                                                                            clearDialog()
                                                                        }}
                                                                        cancelText={i18n.t(
                                                                            'info-dialog.okay'
                                                                        )}
                                                                        confirmText=""
                                                                    />
                                                                ),
                                                                backdropClosable:
                                                                    false,
                                                            })
                                                        }}
                                                    />
                                                </Stack>
                                            </Box>
                                            <ProfileShareSectionFinder
                                                sections={hiddenSections}
                                                control={control}
                                                orgName-={
                                                    selectedOrg?.friendlyName
                                                }
                                                hiddenDetails
                                            />
                                        </Stack>
                                    )}
                            </Stack>
                        )}
                        <Stack
                            padding="5px 0px 10px"
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Controller
                                name="marketingOptIn"
                                defaultValue={false}
                                control={control as any}
                                render={({ field }) => (
                                    <CheckboxField
                                        style={{ marginLeft: '-10px' }}
                                        sx={{ color: ColoursObj.core.primary }}
                                        checked={field.value}
                                        onChange={(v) =>
                                            field.onChange(
                                                v.currentTarget.checked
                                            )
                                        }
                                    />
                                )}
                            />
                            <Typography
                                variant="bodySubtext"
                                color={ColoursObj.core.primary}
                            >
                                {i18n.t('admin.agree')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </NavigationView>
        </FormProvider>
    )
}
